import { parseError } from '@repo/observability/error';
import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';
import { isValidElement } from 'react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

/**
 * Base UI warns in both directions when `nativeButton` disagrees with what a
 * button-like part actually renders. Composing through `render` is the normal
 * way to make a button navigate (`render={<Link />}`), so infer the answer
 * instead of asking every call site to pass it.
 *
 * We can only be sure about two cases: an intrinsic tag tells us directly, and
 * anything carrying an `href` is an anchor (next/link renders one). A wrapper
 * component is opaque from here — our own `<Button>` renders a real `<button>`
 * — so fall back to Base UI's default of `true` rather than guessing wrong.
 */
export const isNativeButtonRender = (render: unknown): boolean => {
  if (!isValidElement(render)) {
    return true;
  }

  if (typeof render.type === 'string') {
    return render.type === 'button';
  }

  const props = render.props as { href?: unknown } | null;

  return !(props && 'href' in props);
};

/**
 * When a button composes a navigation target (`render={<Link href=… />}`), Base
 * UI stamps `role="button"` on it because `nativeButton` is false. That
 * overrides the anchor's implicit `link` role, so assistive tech announces a
 * button and the element loses its link affordances.
 *
 * Returns `'link'` for anything that renders a real anchor, so the caller can
 * put the correct role back. `undefined` leaves the role alone.
 */
export const renderedLinkRole = (render: unknown): 'link' | undefined => {
  if (!isValidElement(render)) {
    return undefined;
  }

  const props = render.props as { href?: unknown } | null;
  const hasHref = Boolean(props && 'href' in props && props.href != null);

  if (typeof render.type === 'string') {
    // Only a real anchor with a destination is a link.
    return render.type === 'a' && hasHref ? 'link' : undefined;
  }

  // A component carrying `href` renders an anchor (next/link and friends).
  return hasHref ? 'link' : undefined;
};

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const handleError = (error: unknown): void => {
  const message = parseError(error);

  toast.error(message);
};
